import type { AlphaRun } from "@nlc/shared-types";

export const ALPHA_BATCH_SIZE = 5;

export type StepState = "active" | "done" | "pending";

export type GenerationStep = {
  label: string;
  state: StepState;
};

export type GenerationProgress = {
  conceptNumber: number;
  percent: number;
  steps: GenerationStep[];
};

const PROMPT_READ_AT_MS = 1500;
const STYLES_MATCHED_AT_MS = 4500;

// Consecutive checkpoints with the same percent are pauses.
const FAKE_TIMELINE = [
  { atMs: 0, percent: 0 },
  { atMs: 1500, percent: 8 },
  { atMs: 3000, percent: 8 },
  { atMs: 4500, percent: 15 },
  { atMs: 6000, percent: 15 },
  { atMs: 10_800, percent: 22 },
  { atMs: 14_800, percent: 22 },
  { atMs: 19_600, percent: 29 },
  { atMs: 23_600, percent: 29 },
  { atMs: 28_400, percent: 36 },
  { atMs: 32_400, percent: 36 },
  { atMs: 37_200, percent: 43 },
  { atMs: 41_200, percent: 43 },
  { atMs: 46_000, percent: 50 },
  { atMs: 50_000, percent: 50 },
  { atMs: 54_800, percent: 57 },
  { atMs: 58_800, percent: 57 },
  { atMs: 63_600, percent: 64 },
  { atMs: 67_600, percent: 64 },
  { atMs: 72_400, percent: 71 },
  { atMs: 76_400, percent: 71 },
  { atMs: 81_200, percent: 78 },
  { atMs: 85_200, percent: 78 },
  { atMs: 90_000, percent: 85 },
  { atMs: 180_000, percent: 92 },
];

const PRE_PLANNING_STATUSES = new Set<AlphaRun["status"]>([
  "reserving",
  "queued",
]);

const TERMINAL_STATUSES = new Set<AlphaRun["status"]>(["delivered", "failed"]);

export function fakePercent(elapsedMs: number): number {
  const nextIndex = FAKE_TIMELINE.findIndex(({ atMs }) => atMs > elapsedMs);

  if (nextIndex === -1) return FAKE_TIMELINE.at(-1)!.percent;

  const previous = FAKE_TIMELINE[nextIndex - 1]!;
  const next = FAKE_TIMELINE[nextIndex]!;
  const progress = (elapsedMs - previous.atMs) / (next.atMs - previous.atMs);

  return previous.percent + (next.percent - previous.percent) * progress;
}

export function generationProgress(
  run: AlphaRun,
  nowMs: number,
): GenerationProgress {
  const elapsedMs = Math.max(0, nowMs - Date.parse(run.createdAt));
  const settled = settledDesignCount(run);
  const allSettled = settled === ALPHA_BATCH_SIZE;
  const isTerminal = TERMINAL_STATUSES.has(run.status);
  const isPastQueue = !PRE_PLANNING_STATUSES.has(run.status);
  const hasReadPrompt = isPastQueue || elapsedMs >= PROMPT_READ_AT_MS;
  const hasMatchedStyles = isPastQueue || elapsedMs >= STYLES_MATCHED_AT_MS;
  const isDesigning =
    elapsedMs >= STYLES_MATCHED_AT_MS || run.status === "rendering";

  return {
    conceptNumber: Math.min(settled + 1, ALPHA_BATCH_SIZE),
    percent: Math.round(
      Math.max(generationPercent(run), fakePercent(elapsedMs)),
    ),
    steps: [
      { label: "Read your prompt", state: hasReadPrompt ? "done" : "active" },
      {
        label: "Matched your styles",
        state: hasMatchedStyles ? "done" : hasReadPrompt ? "active" : "pending",
      },
      {
        label: "Designing concepts",
        state:
          allSettled || isTerminal
            ? "done"
            : isDesigning
              ? "active"
              : "pending",
      },
      {
        label: "Final checks",
        state: isTerminal ? "done" : allSettled ? "active" : "pending",
      },
    ],
  };
}

function generationPercent(run: AlphaRun): number {
  return (settledDesignCount(run) / ALPHA_BATCH_SIZE) * 100;
}

function settledDesignCount(run: AlphaRun): number {
  return run.designs.filter(({ status }) => status !== "pending").length;
}
